import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

const ViewerContainer = styled(Box)(({ theme, isFullscreen, embedded }) => ({
  width: '100%',
  height: embedded ? '100%' : (isFullscreen ? '100vh' : '600px'),
  backgroundColor: '#000',
  borderRadius: embedded ? 0 : (isFullscreen ? 0 : 12),
  overflow: 'hidden',
  position: isFullscreen ? 'fixed' : 'relative',
  top: isFullscreen ? 0 : 'auto',
  left: isFullscreen ? 0 : 'auto',
  right: isFullscreen ? 0 : 'auto',
  bottom: isFullscreen ? 0 : 'auto',
  zIndex: isFullscreen ? 9999 : 1,
  transition: 'all 200ms ease',
  // Responsive heights for different screen sizes (only if not embedded)
  ...(!embedded && {
    [theme.breakpoints.down('lg')]: {
      height: isFullscreen ? '100vh' : '500px',
    },
    [theme.breakpoints.down('md')]: {
      height: isFullscreen ? '100vh' : '400px',
    },
    [theme.breakpoints.down('sm')]: {
      height: isFullscreen ? '100vh' : '300px',
      borderRadius: isFullscreen ? 0 : 8,
    },
    // Large imaging screens (1920px+)
    [theme.breakpoints.up('xl')]: {
      height: isFullscreen ? '100vh' : 'calc(100vh - 200px)',
      minHeight: isFullscreen ? '100vh' : '700px',
    },
  }),
}));

const ViewerIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 'none',
  backgroundColor: '#000',
});

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: '#fff',
});

const ToolbarContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  display: 'flex',
  gap: 8,
  zIndex: 10,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  borderRadius: 8,
  padding: 4,
  backdropFilter: 'blur(8px)',
}));

const ToolbarButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  width: 36,
  height: 36,
  transition: 'all 150ms ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
}));

const PatientBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  left: 12,
  zIndex: 10,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  borderRadius: 8,
  padding: '8px 12px',
  backdropFilter: 'blur(8px)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: 'rgba(255, 255, 255, 0.7)',
}));

/**
 * OHIF Viewer Component
 * Integrates OHIF Viewer for DICOM image viewing
 * Optimized for large imaging screens with fullscreen support
 *
 * @param {string} studyInstanceUID - DICOM Study Instance UID
 * @param {string} orthancStudyId - Orthanc internal study ID
 * @param {string} patientName - Patient name for display
 * @param {boolean} showToolbar - Show viewer toolbar controls
 * @param {string} height - Custom height (overrides responsive defaults)
 * @param {boolean} autoFullscreen - Auto-enter fullscreen on large screens
 * @param {boolean} embedded - Embedded mode for report mode (fills parent container)
 */
const OHIFViewer = ({
  studyInstanceUID,
  orthancStudyId,
  patientName = '',
  showToolbar = true,
  height,
  autoFullscreen = false,
  embedded = false,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Get OHIF viewer URL from environment
  const ohifBaseUrl = process.env.REACT_APP_OHIF_VIEWER_URL || 'http://localhost:3001';

  // Construct OHIF viewer URL
  const getViewerUrl = () => {
    if (studyInstanceUID) {
      return `${ohifBaseUrl}/viewer?StudyInstanceUIDs=${studyInstanceUID}`;
    }
    return `${ohifBaseUrl}/`;
  };

  const viewerUrl = getViewerUrl();

  useEffect(() => {
    if (viewerUrl) {
      setLoading(true);
      setError(null);
    }
  }, [viewerUrl]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore keyboard shortcuts when typing in an input field
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      // F key for fullscreen
      if (e.key === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        toggleFullscreen();
      }
      // Escape to exit fullscreen
      if (e.key === 'Escape' && isFullscreen) {
        exitFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load DICOM viewer. Please try again.');
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    if (iframeRef.current) {
      iframeRef.current.src = viewerUrl;
    }
  };

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current?.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      }
    } else {
      exitFullscreen();
    }
  }, [isFullscreen]);

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  };

  const handleOpenInNewTab = () => {
    if (viewerUrl) {
      window.open(viewerUrl, '_blank');
    }
  };

  if (!studyInstanceUID && !orthancStudyId) {
    return (
      <ViewerContainer sx={height ? { height } : undefined}>
        <EmptyState>
          <BrokenImageIcon sx={{ fontSize: 64, opacity: 0.4, mb: 2 }} />
          <Typography variant="h6" fontWeight={500}>No Study Selected</Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
            Select a study to view images
          </Typography>
        </EmptyState>
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer
      ref={containerRef}
      isFullscreen={isFullscreen}
      embedded={embedded ? 1 : 0}
      sx={height && !isFullscreen && !embedded ? { height } : undefined}
    >
      {/* Patient Info Badge */}
      {patientName && !loading && (
        <PatientBadge>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#10B981',
            }}
          />
          <Typography variant="caption" fontWeight={500}>
            {patientName}
          </Typography>
        </PatientBadge>
      )}

      {/* Toolbar */}
      {showToolbar && !loading && !error && (
        <ToolbarContainer>
          <Tooltip title="Refresh viewer">
            <ToolbarButton onClick={handleRetry} size="small">
              <RefreshIcon fontSize="small" />
            </ToolbarButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit fullscreen (F)" : "Fullscreen (F)"}>
            <ToolbarButton onClick={toggleFullscreen} size="small">
              {isFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </ToolbarButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <ToolbarButton onClick={handleOpenInNewTab} size="small">
              <OpenInNewIcon fontSize="small" />
            </ToolbarButton>
          </Tooltip>
        </ToolbarContainer>
      )}

      {/* Loading State */}
      {loading && (
        <LoadingOverlay>
          <CircularProgress
            size={48}
            thickness={3}
            sx={{ color: 'primary.main' }}
          />
          <Typography variant="body1" fontWeight={500} sx={{ mt: 3 }}>
            Loading DICOM Viewer...
          </Typography>
          {patientName && (
            <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
              {patientName}
            </Typography>
          )}
        </LoadingOverlay>
      )}

      {/* Error State */}
      {error && (
        <LoadingOverlay>
          <BrokenImageIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
          <Typography variant="body1" color="error.main" fontWeight={500}>
            {error}
          </Typography>
          <ToolbarButton
            onClick={handleRetry}
            sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
          >
            <RefreshIcon sx={{ mr: 1 }} />
            <Typography variant="button">Retry</Typography>
          </ToolbarButton>
        </LoadingOverlay>
      )}

      {/* OHIF Viewer Iframe */}
      {viewerUrl && (
        <ViewerIframe
          ref={iframeRef}
          src={viewerUrl}
          title="DICOM Viewer"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          allow="fullscreen"
        />
      )}
    </ViewerContainer>
  );
};

export default OHIFViewer;
